import { useCallback, useEffect, useRef, useState } from "react";
import IStepItem from "models/work-items/IStepItem";
import { IWorkItemDraft } from "models/work-items";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import useWorkItemPermissions from "./useWorkItemPermissions";

const Steps: IStepItem[] = [
    {
        key: 'basic-info',
        label: 'Basic Information',
        enabled: true
    },
    {
        key: 'resources',
        label: 'Resource Allocation'
    },
    {
        key: 'related',
        label: 'Related Work Items'
    },
    {
        key: 'product-master',
        label: 'Product Master'
    },
    {
        key: 'metadata',
        label: 'Metadata'
    },
    {
        key: 'attachments',
        label: 'Attachments'
    },
    {
        key: 'notes',
        label: 'Notes'
    }
];

type AdjustSignature = (steps: IStepItem[]) => IStepItem[];

type WorkItemDraftsResult = [
    () => Promise<IStepItem[]>, // validate
    AdjustSignature // adjust steps
];

type UseWorkItemDraftStepValidationSignature = (draft: IWorkItemDraft | null) => WorkItemDraftsResult;

const useWorkItemDraftStepValidation: UseWorkItemDraftStepValidationSignature = (draft: IWorkItemDraft | null) => {
    const fetcher = useCentralApi();
    const user = useCurrentUser();
    const workItemPermissions = useWorkItemPermissions();
    
    const [filteredSteps, setFilteredSteps] = useState<IStepItem[]>([...Steps]);
    
    const permissionsProcessedRef = useRef<boolean>(false);
    
    useEffect(() => {
        if (!workItemPermissions) return;
        
        if (permissionsProcessedRef.current) return;
        
        let updatedSteps = [...Steps];
        
        if (!(
            workItemPermissions.canLinkProductMaster || 
            workItemPermissions.canUnlinkProductMaster
        )) {
            updatedSteps = updatedSteps.filter(step => step.key !== 'product-master');
        }
        
        setFilteredSteps(updatedSteps);
        
        permissionsProcessedRef.current = true;
    }, [workItemPermissions]);
    
    const adjustSteps = useCallback((steps: IStepItem[]) => {

        const adjustedSteps = [...steps];
        
        for (let step of adjustedSteps) {
            const matchingStep = filteredSteps.find(s => s.key === step.key);
            if (matchingStep) {
                step.label = matchingStep.label;
                step.enabled = true;
            }
        }
        
        return adjustedSteps.filter(step => 
            filteredSteps.some(filteredStep => filteredStep.key === step.key)
        );
    }, [filteredSteps]);

    const validate = useCallback(async () => {
        if (!user || !user.access_token) {
            return filteredSteps;
        }

        if (draft === null || draft.id <= 0) 
            return filteredSteps;

        try {
            const resp = await fetcher(`/api/v3/drafts/workitems/${draft.id}/validate`, {
                method: 'GET'
            });

            const apiSteps = await resp.json() as IStepItem[];
            
            const validSteps = apiSteps.filter(apiStep => 
                filteredSteps.some(filteredStep => filteredStep.key === apiStep.key)
            );
            
            for (let step of validSteps) {
                const matchingStep = filteredSteps.find(s => s.key === step.key);
                if (matchingStep) {
                    step.label = matchingStep.label;
                    step.enabled = true;
                }
            }

            return validSteps;
        } catch (error) {
            console.error('Error validating steps:', error);
            return filteredSteps;
        }
    }, [draft?.id, user, filteredSteps]);

    return [validate, adjustSteps];
};

export default useWorkItemDraftStepValidation;